import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, Stack, Typography, Skeleton, Alert, Button, Chip,
  TextField, IconButton, Tooltip, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  SchoolRounded as AcademicIcon,
  PrintRounded as PrintIcon,
  ZoomInRounded as ZoomInIcon,
  ZoomOutRounded as ZoomOutIcon,
  MenuBookRounded as ReportIcon,
} from '@mui/icons-material';
import API from '../BaseURL';
import { P, CARD_SHADOW } from './portalTheme';
import { reportCardHTML, REPORT_CSS, printableDocument } from '../pages/examination/reportCardHtml';

// ─────────────────────────────────────────────────────────────────
// PortalAcademics — report cards, read like the receipts are.
//
// Same interaction as Account → Receipts, because it is the same job:
// a stack of printed documents the parent scrolls through, with a page
// rail down the left. Learning it once should be enough.
//
// THE MARKUP IS THE SCHOOL'S
//   `reportCardHTML` is imported from the staff examination screens
//   rather than reimplemented. A parent's copy of a report that differs
//   from the one the school printed — even in a grade band — is a
//   dispute, and a lookalike would drift the first time a rule changes.
//
// WHAT THE FILTERS ARE BUILT FROM
//   The year / term / type options come from the score tables
//   themselves, not from a list of terms the school ought to have. A
//   report exists when marks exist; offering a term with nothing behind
//   it produces a blank sheet and a phone call.
// ─────────────────────────────────────────────────────────────────

// A report card is A4 portrait: 210mm ≈ 794px at 96dpi.
const SHEET_PX = 794;
const SHEET_RATIO = 297 / 210;   // A4 height / width

export default function PortalAcademics() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [index, setIndex]     = useState(null);   // what reports exist
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [year, setYear] = useState('');
  const [term, setTerm] = useState('');
  const [type, setType] = useState('');

  // Loaded report payloads, keyed by report id, so flipping back to one
  // already opened is instant and costs no request.
  const [cards, setCards]   = useState({});
  const [busy, setBusy]     = useState({});
  const [active, setActive] = useState(0);
  const [zoom, setZoom]     = useState(1);

  const scrollRef = useRef(null);
  const pageRefs  = useRef([]);
  const railRefs  = useRef([]);

  useEffect(() => {
    let alive = true;
    API.get('/portal/reports')
      .then(r => { if (alive) { setIndex(r.data); setError(''); } })
      .catch(e => { if (alive) setError(e?.response?.data?.message || 'Could not load your reports'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Filtering only removes rows; the newest-first order the server
  // worked out is preserved, so whatever the parent narrows to, the
  // most recent report is still the one they land on.
  const shown = useMemo(() => {
    const all = index?.reports || [];
    return all.filter(r =>
      (!year || r.acc_yer === year)
      && (!term || r.term === term)
      && (!type || r.exam_type === type));
  }, [index, year, term, type]);

  // Fetch the cards on screen. Sequential rather than parallel: each is
  // a full report build on the server, and firing twelve at once on a
  // school's shared host is how a page becomes the slowest thing on it.
  useEffect(() => {
    let alive = true;
    (async () => {
      for (const r of shown) {
        if (!alive) return;
        if (cards[r.id] || busy[r.id]) continue;
        setBusy(b => ({ ...b, [r.id]: true }));
        try {
          const res = await API.get('/portal/reports/card', {
            params: {
              exam_type: r.exam_type, term: r.term,
              acc_yer: r.acc_yer, exam_num: r.exam_num || undefined,
            },
          });
          if (!alive) return;
          setCards(c => ({ ...c, [r.id]: res.data }));
        } catch (e) {
          if (!alive) return;
          setCards(c => ({ ...c, [r.id]: { __error: e?.response?.data?.message || 'Could not build this report' } }));
        } finally {
          if (alive) setBusy(b => ({ ...b, [r.id]: false }));
        }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  useEffect(() => { setActive(0); }, [year, term, type]);

  // Which page is in view — reported by the pages through an observer
  // rather than measured on every scroll event.
  useEffect(() => {
    if (!shown.length || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      const seen = entries.filter(e => e.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!seen) return;
      const i = Number(seen.target.getAttribute('data-page'));
      if (!Number.isNaN(i)) setActive(i);
    }, { root: scrollRef.current || null, threshold: [0.2, 0.5] });
    pageRefs.current.slice(0, shown.length).forEach(el => el && io.observe(el));
    return () => io.disconnect();
  }, [shown.length, cards]);

  useEffect(() => {
    railRefs.current[active]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [active]);

  const goTo = useCallback((i) => {
    pageRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActive(i);
  }, []);

  const printAll = () => {
    const html = shown
      .map(r => cards[r.id])
      .filter(d => d && !d.__error)
      .map(d => reportCardHTML(d))
      .join('');
    if (!html) return;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    w.document.write(printableDocument(html, 'Report cards'));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const clearFilters = () => { setYear(''); setTerm(''); setType(''); };
  const filtered = !!(year || term || type);

  const avail = isDesktop ? 640 : 320;
  const scale = Math.min(1, avail / SHEET_PX) * zoom;

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={72} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={460} sx={{ borderRadius: 3 }} />
      </Stack>
    );
  }
  if (error) return <Alert severity="error" sx={{ borderRadius: 3 }}>{error}</Alert>;

  const nothingAtAll = !index?.reports?.length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 2 }}>
        <Box sx={{ width: 3, height: 20, borderRadius: 2, bgcolor: P.brand }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 800, color: P.ink }}>
            Report cards
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: P.inkSoft }}>
            {nothingAtAll
              ? 'No results published yet'
              : `${index.total} report${index.total === 1 ? '' : 's'} on record`}
          </Typography>
        </Box>
      </Stack>

      {nothingAtAll ? (
        <Card sx={{
          borderRadius: 3, p: 5, textAlign: 'center', boxShadow: 'none',
          border: '1px dashed', borderColor: P.line,
        }}>
          <AcademicIcon sx={{ fontSize: 44, color: P.line }} />
          <Typography sx={{ fontWeight: 800, color: P.ink, mt: 1 }}>
            No report cards yet
          </Typography>
          <Typography sx={{ fontSize: 13, color: P.inkSoft, mt: .5, maxWidth: 420, mx: 'auto' }}>
            A report appears here as soon as the school publishes results for a term.
          </Typography>
        </Card>
      ) : (
        <>
          {/* Filters — only the options that exist. */}
          <Card sx={{
            borderRadius: 3, p: 2, mb: 2, boxShadow: CARD_SHADOW,
            border: '1px solid', borderColor: P.line,
          }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} alignItems={{ sm: 'center' }}>
              <TextField size="small" select SelectProps={{ native: true }} label="Academic year"
                InputLabelProps={{ shrink: true }} value={year}
                onChange={e => setYear(e.target.value)} sx={{ flex: 1 }}>
                <option value="">All years</option>
                {index.years.map(y => <option key={y} value={y}>{y}</option>)}
              </TextField>
              <TextField size="small" select SelectProps={{ native: true }} label="Term"
                InputLabelProps={{ shrink: true }} value={term}
                onChange={e => setTerm(e.target.value)} sx={{ flex: 1 }}>
                <option value="">All terms</option>
                {index.terms.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </TextField>
              <TextField size="small" select SelectProps={{ native: true }} label="Exam type"
                InputLabelProps={{ shrink: true }} value={type}
                onChange={e => setType(e.target.value)} sx={{ flex: 1 }}>
                <option value="">All types</option>
                {index.types.map(t => (
                  <option key={t.key} value={t.key}>{t.label} ({t.count})</option>
                ))}
              </TextField>
              {filtered && (
                <Button onClick={clearFilters} size="small"
                  sx={{ fontWeight: 700, textTransform: 'none', color: P.inkSoft }}>
                  Clear
                </Button>
              )}
            </Stack>
          </Card>

          {shown.length === 0 ? (
            <Card sx={{
              borderRadius: 3, p: 5, textAlign: 'center', boxShadow: 'none',
              border: '1px dashed', borderColor: P.line,
            }}>
              <ReportIcon sx={{ fontSize: 40, color: P.line }} />
              <Typography sx={{ fontWeight: 800, color: P.ink, mt: 1 }}>
                No report matches these filters
              </Typography>
              <Button onClick={clearFilters} sx={{ mt: 1, fontWeight: 800, textTransform: 'none' }}>
                Show all reports
              </Button>
            </Card>
          ) : (
            <>
              {/* Toolbar */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography sx={{ flex: 1, fontSize: 13, color: P.inkSoft }}>
                  Page <b style={{ color: P.ink }}>{active + 1}</b> of {shown.length}
                </Typography>
                <Tooltip title="Zoom out">
                  <span>
                    <IconButton size="small" disabled={zoom <= 0.6}
                      onClick={() => setZoom(z => Math.max(0.6, +(z - 0.2).toFixed(2)))}>
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: P.inkSoft, width: 42, textAlign: 'center' }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom in">
                  <span>
                    <IconButton size="small" disabled={zoom >= 2}
                      onClick={() => setZoom(z => Math.min(2, +(z + 0.2).toFixed(2)))}>
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Button size="small" startIcon={<PrintIcon />} onClick={printAll}
                  sx={{ fontWeight: 800, textTransform: 'none', color: P.brandDeep }}>
                  Print all
                </Button>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="flex-start">
                {/* Page rail */}
                {isDesktop && (
                  <Box sx={{
                    width: 150, flexShrink: 0,
                    maxHeight: 'calc(100vh - 300px)', overflowY: 'auto',
                    pr: .5, '&::-webkit-scrollbar': { width: 6 },
                  }}>
                    <Stack spacing={1}>
                      {shown.map((r, i) => {
                        const d = cards[r.id];
                        const on = active === i;
                        return (
                          <Box key={r.id}
                            ref={el => { railRefs.current[i] = el; }}
                            onClick={() => goTo(i)}
                            sx={{
                              cursor: 'pointer', borderRadius: 1.5, overflow: 'hidden',
                              border: '2px solid', borderColor: on ? P.brand : P.line,
                              bgcolor: '#fff', transition: 'border-color .15s',
                              '&:hover': { borderColor: P.brand },
                            }}>
                            <Box sx={{ height: 108, overflow: 'hidden', bgcolor: '#fff' }}>
                              {d && !d.__error ? (
                                <Box sx={{
                                  transform: 'scale(0.183)', transformOrigin: 'top left',
                                  width: SHEET_PX, pointerEvents: 'none',
                                }} dangerouslySetInnerHTML={{ __html: reportCardHTML(d) }} />
                              ) : (
                                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                  <ReportIcon sx={{ fontSize: 26, color: P.line }} />
                                </Stack>
                              )}
                            </Box>
                            <Box sx={{
                              px: .9, py: .7,
                              bgcolor: on ? P.brandSoft : '#F7FAFE',
                              borderTop: '1px solid', borderColor: P.line,
                            }}>
                              <Stack direction="row" alignItems="center" spacing={.5}>
                                <Typography noWrap sx={{
                                  flex: 1, minWidth: 0, fontSize: 10.5, fontWeight: 800,
                                  color: on ? P.brandDeep : P.ink,
                                }}>
                                  {i + 1}. {r.title}
                                </Typography>
                                {r.is_latest && (
                                  <Box sx={{
                                    px: .6, py: .1, borderRadius: 1, flexShrink: 0,
                                    bgcolor: P.brand, color: '#fff',
                                    fontSize: 8.5, fontWeight: 800, letterSpacing: .3,
                                  }}>
                                    LATEST
                                  </Box>
                                )}
                              </Stack>
                              <Typography noWrap sx={{ fontSize: 9.5, color: P.inkSoft }}>
                                {r.term_label} · {r.acc_yer}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {/* The reports */}
                <Box ref={scrollRef} sx={{
                  flex: 1, minWidth: 0,
                  maxHeight: { xs: 'none', md: 'calc(100vh - 300px)' },
                  overflowY: { xs: 'visible', md: 'auto' },
                  overflowX: 'auto',
                  bgcolor: '#4A5568', borderRadius: 2, p: { xs: 1.2, md: 2 },
                  '&::-webkit-scrollbar': { width: 8, height: 8 },
                }}>
                  <Stack spacing={2} alignItems="center">
                    {shown.map((r, i) => {
                      const d = cards[r.id];
                      return (
                        <Box key={r.id} data-page={i}
                          ref={el => { pageRefs.current[i] = el; }}
                          sx={{ width: SHEET_PX * scale, flexShrink: 0 }}>
                          <Box sx={{
                            width: SHEET_PX * scale,
                            minHeight: SHEET_PX * SHEET_RATIO * scale,
                            overflow: 'hidden', bgcolor: '#fff',
                            boxShadow: '0 4px 18px rgba(0,0,0,.35)',
                            display: d ? 'block' : 'flex',
                            alignItems: 'center', justifyContent: 'center',
                          }}>
                            {!d ? (
                              <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
                                <Skeleton variant="rounded" width={SHEET_PX * scale * .7} height={18} />
                                <Skeleton variant="rounded" width={SHEET_PX * scale * .55} height={14} />
                                <Typography sx={{ fontSize: 12, color: P.inkSoft, mt: 1 }}>
                                  Building {r.title}…
                                </Typography>
                              </Stack>
                            ) : d.__error ? (
                              <Stack alignItems="center" spacing={1} sx={{ p: 4, textAlign: 'center' }}>
                                <ReportIcon sx={{ fontSize: 34, color: P.line }} />
                                <Typography sx={{ fontWeight: 800, color: P.ink }}>
                                  {r.title} could not be built
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: P.inkSoft }}>
                                  {d.__error}
                                </Typography>
                              </Stack>
                            ) : (
                              <Box sx={{
                                transform: `scale(${scale})`, transformOrigin: 'top left',
                                width: SHEET_PX,
                              }} dangerouslySetInnerHTML={{ __html: reportCardHTML(d) }} />
                            )}
                          </Box>
                          <Stack direction="row" justifyContent="center" spacing={.8}
                            sx={{ mt: .8, mb: .4 }}>
                            {r.is_latest && (
                              <Chip size="small" label="MOST RECENT"
                                sx={{
                                  height: 22, fontSize: 10, fontWeight: 800, letterSpacing: .4,
                                  bgcolor: P.brand, color: '#fff',
                                }} />
                            )}
                            <Chip size="small" label={`${r.title} · ${r.term_label} · ${r.acc_yer}`}
                              sx={{
                                height: 22, fontSize: 11, fontWeight: 700,
                                bgcolor: 'rgba(255,255,255,.16)', color: '#fff',
                              }} />
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Stack>
            </>
          )}
        </>
      )}

      {/* The stylesheet the report markup expects. */}
      <style>{REPORT_CSS}</style>
    </Box>
  );
}
